import ColorDisablerButtons from './ui/ColorDisablerButtons';
import ColorDisablerButtonsEvents from './events/ColorDisablerButtonsEvents';
import ColorsPalette, { IColorData } from './ui/ColorsPalette';
import ColorsPaletteEvents from './events/ColorsPaletteEvents';
import Config from './data/Config';
import DesignData from './data/DesignData';
import JerseyImages from './ui/JerseyImages';
import SelectedColorIndicators from './ui/SelectedColorIndicators';
import TabButtonIds from './data/TabButtonIds';
import TabButtons from './ui/TabButtons';
import TextLabels from './ui/TextLabels';
import { getMovieClipByName, initTextKludge, MovieClip, Stage } from './utils/utils';
import submitRequest, { IPostResult } from './utils/submitRequest';
import ISubmitData from './data/ISubmitData';
import SubmitForm from './ui/SubmitForm';
import SubmitFormEvents from './events/SubmitFormEvents';
import ISubmitFormData from './data/ISubmitFormData';
import StatusTextEvents from './events/StatusTextEvents';

export default class Bikeatac {
  // ================================================================================================
  // properties
  // ================================================================================================
  private selectedColorsById: Map<TabButtonIds, IColorData>;

  private tabButtons: TabButtons;

  private colorsPalette: ColorsPalette;

  private jerseyImages: JerseyImages;

  private selectedColorIndicators: SelectedColorIndicators;

  private textLabels: TextLabels;

  private colorDisabledButtons: ColorDisablerButtons;

  private config: Config;

  private submitForm: SubmitForm;

  private _stage: Stage;

  private canvasImageBlob: Blob;

  // ================================================================================================
  // public
  // ================================================================================================

  // ----------------------------------------------------------------------------------------------
  constructor(stage: Stage) {
    initTextKludge();

    this.selectedColorsById = new Map();

    this._stage = stage;

    this.initJerseyImages();
    this.initTextLabels();
    this.initTabButtons();
    this.initColorPalettes();
    this.initJerseyImages();
    this.initSelectedColorIndicators();
    this.initTabSelectionLabels();
    this.initColorDisablerButtons();
    this.setDefaultTabId();
    this.initSubmitForm();

    this.initConfig()
      .then(() => {
        this.setDefaultColors();
        this.initOpenSubmitFormButton();
        this.jerseyImages.visible = true;
      });
  }

  // ----------------------------------------------------------------------------------------------
  get stage(): Stage {
    return (this._stage);
  }

  // ----------------------------------------------------------------------------------------------
  set thirdImagesHideTargets(values: MovieClip[]) {
    this.jerseyImages.thirdImagesHideTargets = values;
  }

  // ================================================================================================
  // private
  // ================================================================================================

  // ----------------------------------------------------------------------------------------------
  private captureCanvasImage(): Promise<void> {
    const CANVAS_ID = 'canvas';
    const canvas = <HTMLCanvasElement> document.getElementById(CANVAS_ID);
    if (!canvas) {
      console.warn(`No canvas element with id "${CANVAS_ID}" was found.`);
      Promise.resolve();
      return;
    }

    canvas.toBlob((blob) => {
      this.canvasImageBlob = blob;
      Promise.resolve();
    });
  }

  // ----------------------------------------------------------------------------------------------
  private getSubmitData(customerFormData: ISubmitFormData): ISubmitData {
    const submitData: ISubmitData = {
      projectId: this.config.data.projectId,
      mainColor: this.selectedColorsById.get(TabButtonIds.MAIN).name,
      secondColor: this.selectedColorsById.get(TabButtonIds.SECOND).name,
      formData: customerFormData,
    };

    if (this.jerseyImages.thirdVisible) {
      submitData.thirdColor = this.selectedColorsById.get(TabButtonIds.THIRD).name;
    } else {
      submitData.thirdColor = '[NONE]';
    }

    return (submitData);
  }

  // ----------------------------------------------------------------------------------------------
  private initColorDisablerButtons(): void {
    this.colorDisabledButtons = new ColorDisablerButtons(this._stage);

    const events = this.colorDisabledButtons.events;
    events.on(ColorDisablerButtonsEvents.MAIN, this.onColorDisablerMain, this);
    events.on(ColorDisablerButtonsEvents.SECOND, this.onColorDisablerSecond, this);
    events.on(ColorDisablerButtonsEvents.THIRD, this.onColorDisablerThird, this);

    events.on(ColorDisablerButtonsEvents.DISABLER_BUTTON_OVER, this.onColorDisablerOver, this);
  }

  // ----------------------------------------------------------------------------------------------
  private initColorPalettes(): void {
    this.colorsPalette = new ColorsPalette(this._stage);

    const events = this.colorsPalette.events;
    events.on(ColorsPaletteEvents.COLOR_POINTER_OVER, this.onColorsPaletteColorOver, this);
    events.on(ColorsPaletteEvents.COLOR_SELECTED, this.onColorsPaletteColorSelected, this);
  }

  // ----------------------------------------------------------------------------------------------
  private async initConfig(): Promise<void> {
    this.config = new Config();
    try {
      await this.config.load('config.json');
    } catch (error) {
      console.warn(error);
    }
  }

  // ----------------------------------------------------------------------------------------------
  private initJerseyImages(): void {
    this.jerseyImages = new JerseyImages(this._stage);
    this.jerseyImages.visible = false;
  }

  // ----------------------------------------------------------------------------------------------
  private initOpenSubmitFormButton(): void {
    const BUTTON_NAME = 'btn_open_submit_form';
    const button = getMovieClipByName(this.stage, BUTTON_NAME);
    if (!button) {
      console.warn(`No open submit form button named ${BUTTON_NAME} was found.`);
      return;
    }

    button.on('click', () => {
      this.onSubmitPressed();
    });
  }

  // ----------------------------------------------------------------------------------------------
  private initSelectedColorIndicators(): void {
    this.selectedColorIndicators = new SelectedColorIndicators(this._stage);
  }

  // ----------------------------------------------------------------------------------------------
  private initSubmitForm(): void {
    this.submitForm = new SubmitForm(this.stage);
    this.submitForm.events.on(SubmitFormEvents.COMPLETE, this.onSubmitFormComplete, this);
  }

  // ----------------------------------------------------------------------------------------------
  private initTabButtons(): void {
    this.tabButtons = new TabButtons(this._stage);
    this.tabButtons.selectedTabButtonId = TabButtonIds.MAIN;
  }

  // ----------------------------------------------------------------------------------------------
  private initTabSelectionLabel(name: string, id: TabButtonIds): void {
    const button = getMovieClipByName(this._stage, name);
    if (button) {
      button.addEventListener('click', () => {
        this.tabButtons.selectedTabButtonId = id;
      });
    }
  }

  // ----------------------------------------------------------------------------------------------
  private initTabSelectionLabels(): void {
    this.initTabSelectionLabel('btnMainLabel', TabButtonIds.MAIN);
    this.initTabSelectionLabel('btnSecondLabel', TabButtonIds.SECOND);
    this.initTabSelectionLabel('btnThirdLabel', TabButtonIds.THIRD);
  }

  // ----------------------------------------------------------------------------------------------
  private initTextLabels(): void {
    this.textLabels = new TextLabels(this._stage);
  }

  // ----------------------------------------------------------------------------------------------
  private onColorDisablerMain(): void {
    this.jerseyImages.mainVisible = false;
    this.textLabels.main = DesignData.NO_COLOR_SELECTED;
  }

  // ----------------------------------------------------------------------------------------------
  private onColorDisablerOver(): void {
    this.textLabels.working = DesignData.REMOVE_GRADIENT_LABEL;
  }

  // ----------------------------------------------------------------------------------------------
  private onColorDisablerSecond(): void {
    this.jerseyImages.secondVisible = false;
    this.textLabels.second = DesignData.NO_COLOR_SELECTED;
  }

  // ----------------------------------------------------------------------------------------------
  private onColorDisablerThird(): void {
    this.jerseyImages.thirdVisible = false;
    this.textLabels.third = DesignData.NO_COLOR_SELECTED;
  }

  // ----------------------------------------------------------------------------------------------
  private onColorsPaletteColorOver(colorData: IColorData): void {
    this.textLabels.working = colorData.name;
  }

  // ----------------------------------------------------------------------------------------------
  private onColorsPaletteColorSelected(colorData: IColorData): void {
    switch (this.tabButtons.selectedTabButtonId) {
      case TabButtonIds.SECOND:
        this.setSecondColor(colorData);
        break;

      case TabButtonIds.THIRD:
        this.setThirdColor(colorData);
        break;

      case TabButtonIds.MAIN:
      default:
        this.setMainColor(colorData);
        break;
    }
  }

  // ----------------------------------------------------------------------------------------------
  private onSubmitFormComplete(data: ISubmitFormData): void {
    const url = this.config.data.submitRequest.url;
    if (!url) {
      console.warn('No "submitButton.url" property is missing from the config data.');
      this.submitForm.statusText.error = 'Unable to submit request.';
      return;
    }

    this.submitForm.enabled = false;

    let result: IPostResult;
    const submitData = this.getSubmitData(data);

    createjs.Ticker.on('tick', async () => {
      try {
        this.submitForm.statusText.progress = 'Submitting your selections...';
        result = await submitRequest(url, this.canvasImageBlob, submitData);
      } catch (exc) {
        console.warn('subRequest exception:', exc);
      }

      if (result.success) {
        this.submitForm.statusText.success = 'Success!';
        this.submitForm.statusText.events.once(StatusTextEvents.COMPLETE, () => {
          this.submitForm.unpresent();
        });
      } else {
        this.submitForm.statusText.error = result.reason || 'Whoops! Something went wrong...';
        this.submitForm.enabled = true;
      }
    }, this, true);
  }

  // ----------------------------------------------------------------------------------------------
  private async onSubmitPressed(): Promise<void> {
    await this.captureCanvasImage();
    this.openSubmitForm();
  }

  // ----------------------------------------------------------------------------------------------
  private openSubmitForm(): void {
    this.submitForm.present();
  }

  // ----------------------------------------------------------------------------------------------
  private setDefaultColors(): void {
    this.setDefaultColorsMain();
    this.setDefaultColorsSecond();
    this.setDefaultColorsThird();
  }

  // ----------------------------------------------------------------------------------------------
  private setDefaultColorsMain(): void {
    const colorData = this.colorsPalette.getColorByName(this.config.data.mainColor);
    if (colorData) {
      this.setMainColor(colorData);
    }
  }

  // ----------------------------------------------------------------------------------------------
  private setDefaultColorsSecond(): void {
    const colorData = this.colorsPalette.getColorByName(this.config.data.secondColor);
    if (colorData) {
      this.setSecondColor(colorData);
    }
  }

  // ----------------------------------------------------------------------------------------------
  private setDefaultColorsThird(): void {
    const colorData = this.colorsPalette.getColorByName(this.config.data.thirdColor);
    if (colorData) {
      this.setThirdColor(colorData);
    }
  }

  // ----------------------------------------------------------------------------------------------
  private setDefaultTabId(): void {
    this.tabButtons.selectedTabButtonId = DesignData.DEFAULT_SELECTED_TAB;
  }

  // ----------------------------------------------------------------------------------------------
  private setMainColor(colorData: IColorData): void {
    this.selectedColorsById.set(TabButtonIds.MAIN, colorData);
    this.textLabels.main = colorData.name;
    this.jerseyImages.mainColor = colorData.color;
    this.selectedColorIndicators.mainColor = colorData.color;
  }

  // ----------------------------------------------------------------------------------------------
  private setSecondColor(colorData: IColorData): void {
    this.selectedColorsById.set(TabButtonIds.SECOND, colorData);
    this.textLabels.second = colorData.name;
    this.jerseyImages.secondColor = colorData.color;
    this.selectedColorIndicators.secondColor = colorData.color;
  }

  // ----------------------------------------------------------------------------------------------
  private setThirdColor(colorData: IColorData): void {
    this.selectedColorsById.set(TabButtonIds.THIRD, colorData);
    this.textLabels.third = colorData.name;
    this.jerseyImages.thirdColor = colorData.color;
    this.selectedColorIndicators.thirdColor = colorData.color;
  }
}
